import React, { useState, useEffect, useCallback } from 'react';
import { Printer, RefreshCw, Loader2, Wifi, WifiOff, Settings } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { listNetworkPrinters } from '@/services/networkPrintApi';

// Reusable network-printer picker. Fetches online printers reported by the
// Print Bridge agents and lets the operator pick one; remembers the last
// choice as their default. Returns { agentId, printerName, agentName }.
export const NetworkPrinterDialog = ({ open, onOpenChange, onConfirm, title = 'Select a network printer' }) => {
  const [loading, setLoading] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNetworkPrinters();
      const list = data.printers || [];
      setPrinters(list);
      const pref = data.default_pref;
      const onlineList = list.filter((p) => p.online);
      // Prefer saved default (if online), else first online printer.
      let pick = '';
      if (pref) {
        const match = onlineList.find(
          (p) => p.agent_id === pref.agent_id && p.printer_name === pref.printer_name,
        );
        if (match) pick = match.printer_id;
      }
      if (!pick && onlineList.length) pick = onlineList[0].printer_id;
      setSelectedId(pick);
    } catch (e) {
      setPrinters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleConfirm = () => {
    const chosen = printers.find((p) => p.printer_id === selectedId);
    if (!chosen) return;
    onConfirm({
      agentId: chosen.agent_id,
      printerName: chosen.printer_name,
      agentName: chosen.agent_name,
    });
  };

  const onlineCount = printers.filter((p) => p.online).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="network-printer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-[#2D5F4F]" /> {title}
          </DialogTitle>
          <DialogDescription>
            Pick the printer at your station. Your choice is saved as your default.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-gray-500" data-testid="printer-list-loading">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading printers…
            </div>
          )}

          {!loading && onlineCount === 0 && (
            <div className="text-center py-8 text-sm text-gray-500" data-testid="printer-list-empty">
              <Settings className="w-6 h-6 mx-auto mb-2 opacity-50" />
              {printers.length === 0 ? (
                <>
                  No print stations found. Ask an admin to add a station in
                  <strong> Admin → Print Stations</strong> and run the station app.
                </>
              ) : (
                <>No printers are online right now. Start the station app on the printer PC, then tap <strong>Refresh</strong>.</>
              )}
            </div>
          )}

          {!loading && printers.map((p) => {
            const isSel = p.printer_id === selectedId;
            return (
              <button
                key={p.printer_id}
                type="button"
                disabled={!p.online}
                onClick={() => p.online && setSelectedId(p.printer_id)}
                data-testid={`printer-option-${p.printer_id}`}
                className={`w-full text-left rounded-lg border px-3 py-2 flex items-center justify-between transition-colors ${
                  isSel ? 'border-[#2D5F4F] bg-[#2D5F4F]/5 ring-1 ring-[#2D5F4F]' : 'border-gray-200'
                } ${!p.online ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.printer_name}</div>
                  <div className="text-xs text-gray-500 truncate">{p.agent_name}</div>
                </div>
                <span className={`flex items-center gap-1 text-xs ${p.online ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {p.online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                  {p.online ? 'Online' : 'Offline'}
                </span>
              </button>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="printer-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="printer-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedId || onlineCount === 0}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="printer-confirm-btn"
          >
            <Printer className="w-4 h-4 mr-1" /> Use this printer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NetworkPrinterDialog;
